import React from "react";
import { Button } from "@/components/design-system/Button/Button";


export type HeroCta = {
  readonly label: string;
  readonly onClick: () => void;
};

export type HeroSectionProps = {
  readonly eyebrow?: string;
  readonly headline: React.ReactNode;
  readonly description: string;
  readonly primaryCta: HeroCta;
  readonly secondaryCta?: HeroCta;
  readonly backgroundImage?: string;
  readonly imageClassName?: string;
};

export const HeroSection: React.FC<HeroSectionProps> = ({
  eyebrow,
  headline,
  description,
  primaryCta,
  secondaryCta,
  backgroundImage,
  imageClassName = "absolute inset-0 h-full w-full object-cover object-center",
}) => (
  <section className="relative w-full overflow-hidden bg-primary-950 md:h-160">
    {backgroundImage && (
      <>
        {/* Mobile: image stacked at top, fades into dark background */}
        {/* Gradient: transparent 0-51%, fade 51-92%, solid #220f2e 92-100% (matches Figma node 1841:1670) */}
        <div className="relative h-70 w-full md:hidden">
          <img
            src={backgroundImage}
            alt=""
            aria-hidden
            className="h-full w-full object-cover object-center"
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: "linear-gradient(to bottom, transparent 51%, #220f2e 92%)" }}
          />
        </div>

        {/* Desktop: image positioned per imageClassName */}
        <img
          src={backgroundImage}
          alt=""
          aria-hidden
          className={`hidden md:block ${imageClassName}`}
        />
      </>
    )}

    {/* Desktop: left-to-right gradient — solid dark 0-31%, fades to transparent at 63% (matches Figma node 1830:1842) */}
    <div
      className="pointer-events-none absolute inset-0 hidden md:block"
      style={{ background: "linear-gradient(to right, #220f2e 31%, transparent 63%)" }}
    />

    <div className="section-inset relative z-10 py-8 md:flex md:h-full md:flex-col md:justify-center">
      <div className="w-full md:max-w-[618px]">
        {eyebrow && (
          <p className="caption-semibold mb-4 uppercase tracking-widest text-white/60 md:mb-6">
            {eyebrow}
          </p>
        )}
        <h1 className="headline-d-extrabold mb-4 text-white md:headline-b-extrabold md:mb-8">
          {headline}
        </h1>
        <p className="body-a-regular mb-6 text-white/90 md:subtitle-b-regular md:mb-8 md:max-w-[503px]">
          {description}
        </p>
        <div className="flex gap-4">
          <Button
            variant="solid"
            size="lg"
            onClick={primaryCta.onClick}
            className="flex-1 md:flex-none"
          >
            {primaryCta.label}
          </Button>
          {secondaryCta && (
            <Button
              variant="outline"
              size="lg"
              onClick={secondaryCta.onClick}
              className="flex-1 border-white text-white hover:border-white hover:bg-white/10 hover:text-white md:flex-none"
            >
              {secondaryCta.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  </section>
);
